import React, { useState } from 'react';
import './IMRPage.css';
import martinLogo from '../../assets/Martin-red.png';
import imrPricing from '../../data/imrPricing.json';
import useSeo from '../../helpers/useSeo';

const IMR_EMAIL_ENDPOINT = process.env.REACT_APP_EMAILER_ENDPOINT || 'https://emailer.martinadvertising.com/';

const CAMPAIGN_OPTIONS = [
  {
    id: '10000',
    label: '$10,000 - 200,000 Emails (2 Drops) + 100,000 Social Retargeting Impressions + Sales Matches'
  },
  {
    id: '7500',
    label: '$7,500 - 150,000 Emails (2 Drops) + 75,000 Social Retargeting Impressions + Sales Matches'
  },
  {
    id: '5000',
    label: '$5,000 - 100,000 Emails (2 Drops) + 50,000 Social Retargeting Impressions + Sales Matches'
  },
  {
    id: 'full-market-quote',
    label: 'Full Market Coverage Quote'
  }
];

const TIER_LABELS = {
  entry: 'Entry',
  growth: 'Growth',
  premiumReach: 'Premium Reach',
  fullMarketDominance: 'Full Market Dominance'
};

const TIER_ORDER = ['entry', 'growth', 'premiumReach', 'fullMarketDominance'];

const COMPLIANCE_STATEMENT = 'With submission of the above information, the dealer and/or dealer contact allows Martin Advertising to submit for the selected iMR sales amount to be debited from the dealership iMR portal. The funds will be used in GM compliant Conquest Email campaigns within the same calendar month of the submission. California and commercial campaigns not subject to sales match.';

const formatCost = (cost) =>
  cost.toLocaleString('en-US', { style: 'currency', currency: 'USD' });

const formatNumber = (n) =>
  n.toLocaleString('en-US');

const IMRPage = () => {
  useSeo({
    title: 'iMR Conquest Email Approval',
    description: 'GM dealer iMR Conquest Email approval form for Martin Advertising campaigns.',
    path: '/imr',
    noIndex: true,
  });

  const [formData, setFormData] = useState({
    dealerName: '',
    dealerContact: '',
    bacCode: '',
    dealerEmail: '',
    emailCampaigns: [],
    campaignOption: '',
    complianceConsent: false
  });

  const [gmcTier, setGmcTier] = useState('');
  const [buickTier, setBuickTier] = useState('');
  const [gmcOpen, setGmcOpen] = useState(true);
  const [buickOpen, setBuickOpen] = useState(true);
  const [dealerData, setDealerData] = useState(null);
  const [submitStatus, setSubmitStatus] = useState('idle');
  const [step, setStep] = useState('form');
  const [creativeApproved, setCreativeApproved] = useState(false);

  const getCampaignLabelById = (campaignId) => {
    const selected = CAMPAIGN_OPTIONS.find(option => option.id === campaignId);
    return selected ? selected.label : '';
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === 'bacCode') {
      const matched = imrPricing[value] || null;
      setDealerData(matched);
      setGmcTier('');
      setBuickTier('');
      setGmcOpen(true);
      setBuickOpen(true);
      setFormData(prev => ({
        ...prev,
        bacCode: value,
        dealerName: matched ? matched.dealership : prev.dealerName
      }));
      return;
    }

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleCampaignToggle = (campaign) => {
    setFormData(prev => {
      const campaigns = prev.emailCampaigns.includes(campaign)
        ? prev.emailCampaigns.filter(c => c !== campaign)
        : [...prev.emailCampaigns, campaign];
      return { ...prev, emailCampaigns: campaigns };
    });
  };

  const handleTierSelect = (brand, tierKey) => {
    if (brand === 'gmc') {
      setGmcTier(tierKey);
      setGmcOpen(false);
    } else {
      setBuickTier(tierKey);
      setBuickOpen(false);
    }
  };

  const handleReview = (e) => {
    e.preventDefault();

    const gmcSelected = formData.emailCampaigns.includes('GMC Sierra Email');
    const buickSelected = formData.emailCampaigns.includes('Buick Envision Email');

    if (dealerData) {
      if (gmcSelected && dealerData.gmc && !gmcTier) {
        alert('Please select a GMC tier.');
        return;
      }
      if (buickSelected && dealerData.buick && !buickTier) {
        alert('Please select a Buick tier.');
        return;
      }
    } else {
      if (!formData.campaignOption) {
        alert('Please select a campaign option.');
        return;
      }
    }

    setStep('preview');
    window.scrollTo(0, 0);
  };

  const handleSubmit = async () => {
    if (!creativeApproved) {
      alert('Please review and approve the creative before submitting.');
      return;
    }

    setSubmitStatus('submitting');

    const data = new FormData();
    data.append('formName', 'IMRApproval');
    data.append('dealerName', formData.dealerName);
    data.append('dealerContact', formData.dealerContact);
    data.append('bacCode', formData.bacCode);
    data.append('dealerEmail', formData.dealerEmail);
    data.append('emailCampaigns', JSON.stringify(formData.emailCampaigns));
    data.append('complianceConsent', String(formData.complianceConsent));
    data.append('complianceStatement', COMPLIANCE_STATEMENT);

    if (dealerData) {
      data.append('dealerCity', dealerData.city || '');
      data.append('dealerState', dealerData.state || '');
      data.append('dealerSubregion', dealerData.subregion || '');

      const gmcSelected = formData.emailCampaigns.includes('GMC Sierra Email');
      const buickSelected = formData.emailCampaigns.includes('Buick Envision Email');

      if (gmcSelected && dealerData.gmc && gmcTier) {
        const tier = dealerData.gmc.tiers[gmcTier];
        data.append('gmcTierKey', gmcTier);
        data.append('gmcTierLabel', TIER_LABELS[gmcTier]);
        data.append('gmcTierCost', String(tier.cost));
      }
      if (buickSelected && dealerData.buick && buickTier) {
        const tier = dealerData.buick.tiers[buickTier];
        data.append('buickTierKey', buickTier);
        data.append('buickTierLabel', TIER_LABELS[buickTier]);
        data.append('buickTierCost', String(tier.cost));
      }
    } else {
      data.append('campaignOptionId', formData.campaignOption);
      data.append('campaignOptionLabel', getCampaignLabelById(formData.campaignOption));
    }

    try {
      const response = await fetch(IMR_EMAIL_ENDPOINT, {
        method: 'POST',
        body: data,
        mode: 'cors'
      });

      if (!response.ok) {
        throw new Error(`IMR submission failed with status ${response.status}`);
      }

      setSubmitStatus('submitted');
      setTimeout(() => {
        setFormData({
          dealerName: '',
          dealerContact: '',
          bacCode: '',
          dealerEmail: '',
          emailCampaigns: [],
          campaignOption: '',
          complianceConsent: false
        });
        setGmcTier('');
        setBuickTier('');
        setGmcOpen(true);
        setBuickOpen(true);
        setDealerData(null);
        setStep('form');
        setCreativeApproved(false);
        setSubmitStatus('idle');
      }, 2000);
    } catch (error) {
      console.error('IMR form submission failed:', error);
      setSubmitStatus('failed');
      setTimeout(() => setSubmitStatus('idle'), 2500);
    }
  };

  const getButtonText = () => {
    switch (submitStatus) {
      case 'submitting': return 'Submitting...';
      case 'submitted': return 'Submitted!';
      case 'failed': return 'Submission Failed';
      default: return 'Submit';
    }
  };

  const showGmcSection =
    formData.emailCampaigns.includes('GMC Sierra Email') && dealerData?.gmc;
  const showBuickSection =
    formData.emailCampaigns.includes('Buick Envision Email') && dealerData?.buick;

  const renderTierSection = (brand, brandData, selectedTier, isOpen, onToggle) => {
    const tierLabel = selectedTier ? TIER_LABELS[selectedTier] : null;
    const tierCost = selectedTier ? brandData.tiers[selectedTier]?.cost : null;
    const brandName = brand === 'gmc' ? 'GMC Sierra' : 'Buick Envision';

    return (
      <div className="imr-brand-section" key={brand}>
        <div
          className={`imr-brand-header ${!isOpen ? 'collapsed' : ''}`}
          onClick={onToggle}
        >
          <span className="imr-brand-header-name">{brandName}</span>
          {!isOpen && tierLabel && (
            <span className="imr-brand-header-summary">
              {tierLabel} — {formatCost(tierCost)}
            </span>
          )}
          <span className={`imr-chevron ${isOpen ? 'open' : ''}`}>&#8964;</span>
        </div>

        {isOpen && (
          <div className="imr-tier-options">
            {TIER_ORDER.map((tierKey) => {
              const tier = brandData.tiers[tierKey];
              if (!tier) return null;
              const isSelected = selectedTier === tierKey;
              return (
                <div
                  key={tierKey}
                  className={`imr-tier-option ${isSelected ? 'selected' : ''}`}
                  onClick={() => handleTierSelect(brand, tierKey)}
                >
                  <div className="imr-tier-option-top">
                    <input
                      type="radio"
                      readOnly
                      checked={isSelected}
                      name={`${brand}Tier`}
                      value={tierKey}
                    />
                    <span className="imr-tier-name">{TIER_LABELS[tierKey]}</span>
                    <span className="imr-tier-cost">{formatCost(tier.cost)}</span>
                  </div>
                  <div className="imr-tier-meta">
                    <span>{formatNumber(tier.emailCounts)} Emails</span>
                    <span>{formatNumber(tier.impressions)} Impressions</span>
                    {tier.salesMatch && (
                      <span className="imr-sales-match-badge">Sales Match</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const renderCreativeCard = (brand, dealerName, tierKey, tierData) => {
    const brandLabel = brand === 'gmc' ? 'GMC Sierra Email' : 'Buick Envision Email';
    return (
      <div className="imr-creative-card" key={brand}>
        <div className="imr-creative-card-header">
          <span className="imr-creative-brand-label">{brandLabel}</span>
          <span className="imr-creative-dealer-name">{dealerName}</span>
        </div>
        <div className="imr-creative-placeholder">
          <p>📧 Email creative preview coming soon</p>
          <p className="imr-creative-placeholder-dealer">{dealerName}</p>
        </div>
        {tierData && (
          <div className="imr-creative-tier-summary">
            <span>{TIER_LABELS[tierKey]}</span>
            <span>{formatCost(tierData.cost)}</span>
            <span>{formatNumber(tierData.emailCounts)} Emails</span>
            <span>{formatNumber(tierData.impressions)} Impressions</span>
            {tierData.salesMatch && <span className="imr-sales-match-badge">Sales Match</span>}
          </div>
        )}
      </div>
    );
  };

  const bacTyped = formData.bacCode.length > 0;

  return (
    <div className="imr-page">
      <div className="imr-overlay"></div>

      {step === 'preview' ? (
        <div className="imr-preview-container">
          <img src={martinLogo} alt="Martin Advertising" className="imr-logo" />
          <h1 className="imr-title">Review Your Email Creative</h1>
          <p className="imr-preview-subhead">
            Reviewing creative for: <strong>{formData.dealerName}</strong>
          </p>

          {dealerData ? (
            <>
              {formData.emailCampaigns.includes('GMC Sierra Email') && dealerData.gmc &&
                renderCreativeCard('gmc', formData.dealerName, gmcTier, dealerData.gmc.tiers[gmcTier])
              }
              {formData.emailCampaigns.includes('Buick Envision Email') && dealerData.buick &&
                renderCreativeCard('buick', formData.dealerName, buickTier, dealerData.buick.tiers[buickTier])
              }
            </>
          ) : (
            <div className="imr-creative-card">
              <div className="imr-creative-card-header">
                <span className="imr-creative-brand-label">{getCampaignLabelById(formData.campaignOption)}</span>
              </div>
              <div className="imr-creative-placeholder">
                <p>📧 Email creative preview coming soon</p>
                <p className="imr-creative-placeholder-dealer">{formData.dealerName}</p>
              </div>
              <div className="imr-creative-tier-summary">
                <span>{getCampaignLabelById(formData.campaignOption)}</span>
              </div>
            </div>
          )}

          <div className="imr-creative-approval">
            <label className="imr-compliance-checkbox">
              <input
                type="checkbox"
                checked={creativeApproved}
                onChange={e => setCreativeApproved(e.target.checked)}
              />
              <span>I have reviewed and approve the above creative for submission.</span>
            </label>
          </div>

          <div className="imr-step-actions">
            <button type="button" className="imr-back-btn" onClick={() => setStep('form')}>
              ← Back
            </button>
            <button
              type="button"
              className={`imr-submit-btn ${submitStatus}`}
              onClick={handleSubmit}
              disabled={submitStatus !== 'idle'}
            >
              {getButtonText()}
            </button>
          </div>
        </div>
      ) : (
        <div className="imr-form-container">
          <img src={martinLogo} alt="Martin Advertising" className="imr-logo" />
          <h1 className="imr-title">NER iMR Conquest Email Approval</h1>
          <p className="imr-banner-copy">
            Martin Advertising will submit selected iMR funds for GM-compliant
            Conquest Email campaigns within the same calendar month.
          </p>

          <form onSubmit={handleReview} className="imr-form">
            <div className="imr-input-row">
              <div className="imr-input-group">
                <label htmlFor="dealerName">Dealer Name</label>
                <input
                  type="text"
                  id="dealerName"
                  name="dealerName"
                  value={formData.dealerName}
                  onChange={handleChange}
                  required
                  placeholder="Dealer Name"
                />
              </div>

              <div className="imr-input-group">
                <label htmlFor="dealerContact">Dealer Contact</label>
                <input
                  type="text"
                  id="dealerContact"
                  name="dealerContact"
                  value={formData.dealerContact}
                  onChange={handleChange}
                  required
                  placeholder="First Last"
                />
              </div>
            </div>

            <div className="imr-input-group">
              <label htmlFor="bacCode">BAC Code</label>
              <input
                type="number"
                id="bacCode"
                name="bacCode"
                value={formData.bacCode}
                onChange={handleChange}
                required
                placeholder="Enter BAC code"
              />
            </div>

            <div className="imr-input-group">
              <label htmlFor="dealerEmail">Dealer Email</label>
              <input
                type="email"
                id="dealerEmail"
                name="dealerEmail"
                value={formData.dealerEmail}
                onChange={handleChange}
                required
                placeholder="Enter dealer email"
              />
            </div>

            <div className="imr-input-group">
              <label>Email Campaign</label>
              <div className="imr-toggle-buttons">
                <button
                  type="button"
                  className={`imr-toggle-btn ${formData.emailCampaigns.includes('GMC Sierra Email') ? 'active' : ''}`}
                  onClick={() => handleCampaignToggle('GMC Sierra Email')}
                >
                  GMC Sierra Email
                </button>
                <button
                  type="button"
                  className={`imr-toggle-btn ${formData.emailCampaigns.includes('Buick Envision Email') ? 'active' : ''}`}
                  onClick={() => handleCampaignToggle('Buick Envision Email')}
                >
                  Buick Envision Email
                </button>
              </div>
            </div>

            {/* Dynamic pricing sections when dealerData is found */}
            {(showGmcSection || showBuickSection) && (
              <div className="imr-input-group">
                <label>Campaign Options</label>
                {showGmcSection && renderTierSection(
                  'gmc',
                  dealerData.gmc,
                  gmcTier,
                  gmcOpen,
                  () => setGmcOpen(o => !o)
                )}
                {showBuickSection && renderTierSection(
                  'buick',
                  dealerData.buick,
                  buickTier,
                  buickOpen,
                  () => setBuickOpen(o => !o)
                )}
              </div>
            )}

            {/* Fallback static options */}
            {!dealerData && (formData.emailCampaigns.length > 0 || !bacTyped) && (
              <div className="imr-input-group">
                <label>Campaign Options</label>
                <div className="imr-radio-options">
                  {CAMPAIGN_OPTIONS.map((option) => (
                    <label
                      key={option.id}
                      className={`imr-radio-option ${formData.campaignOption === option.id ? 'selected' : ''}`}
                    >
                      <input
                        type="radio"
                        name="campaignOption"
                        value={option.id}
                        checked={formData.campaignOption === option.id}
                        onChange={handleChange}
                      />
                      <span>{option.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            <div className="imr-compliance">
              <label className="imr-compliance-checkbox" htmlFor="complianceConsent">
                <input
                  type="checkbox"
                  id="complianceConsent"
                  name="complianceConsent"
                  checked={formData.complianceConsent}
                  onChange={handleChange}
                  required
                />
                <span>{COMPLIANCE_STATEMENT}</span>
              </label>
            </div>

            <button
              type="submit"
              className="imr-submit-btn"
            >
              Next →
            </button>
          </form>
        </div>
      )}
    </div>
  );
};

export default IMRPage;
